import { Component } from "react";

import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Field, TextInput, TextArea, SelectInput } from "@/components/Fields";
import {
  BACKEND, getPlan, createPlan, updatePlan, getPlanesMeta, getSector,
  listEmpresas, createEmpresa, updateEmpresa, uploadPlanFoto, deletePlanFoto, analizarPlan, getEstadoAnalisis, generarPlan, eliminarDocumentoPlan,
  generarInformeVisita, eliminarInformeVisita,
  generarDocumentosIndividuales, generarRecordatoriosDesdePlan, generarFormacionTrabajador, borrarDocsTrabajador, borrarTodosDocsTrabajadores,
  generarFormacionBatch, getFormacionBatch,
  crearFormulario, listarFormularios, eliminarFormulario, importarFormulario, formularioPdfUrl,
  iniciarPreanalisis, getPreanalisis,
} from "@/lib/prevelis";
import { toast } from "sonner";
import {
  Building2, MapPin, Users, ClipboardCheck, Sparkles, CheckSquare, FileDown,
  Plus, Trash2, Check, X, Minus, ImagePlus, ChevronLeft, ChevronRight, Loader2,
  Pencil, AlertTriangle, FileText, IdCard, Download, ChevronDown, BellRing,
  Link2, Copy, MessageCircle, Mail, Send, Image as ImageIcon, Video as VideoIcon, DownloadCloud,
} from "lucide-react";

const ESTADO_FORM = {
  no_enviado: { label: "No enviado", cls: "bg-slate-100 text-slate-600" },
  enviado: { label: "Enviado", cls: "bg-blue-50 text-blue-700" },
  abierto: { label: "Abierto", cls: "bg-blue-50 text-blue-700" },
  en_progreso: { label: "En progreso", cls: "bg-amber-50 text-amber-700" },
  completado: { label: "Completado", cls: "bg-emerald-50 text-emerald-700" },
  revisado: { label: "Revisado", cls: "bg-slate-100 text-slate-500" },
};

const STEPS = [
  { key: 1, label: "Empresa", icon: Building2 },
  { key: 2, label: "Centro", icon: MapPin },
  { key: 3, label: "Puestos y trabajadores", icon: Users },
  { key: 4, label: "Visita técnica", icon: ClipboardCheck },
  { key: 5, label: "Análisis IA", icon: Sparkles },
  { key: 6, label: "Revisión técnica", icon: CheckSquare },
  { key: 7, label: "Generar plan", icon: FileDown },
  { key: 8, label: "Documentos individuales", icon: IdCard },
];

const uid = () => Math.random().toString(36).slice(2, 10);
const img = (url) => (url?.startsWith("http") ? url : `${BACKEND}${url}`);

class StepErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("Error al renderizar el paso del asistente:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div data-testid="wizard-step-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="font-semibold mb-1">Se produjo un error al mostrar este paso.</p>
          <p className="text-xs text-red-600 mb-2">Comparte este mensaje con soporte para resolverlo:</p>
          <pre className="whitespace-pre-wrap break-words text-xs bg-white border border-red-200 rounded p-2">{String(this.state.error?.message || this.state.error)}</pre>
          <button onClick={() => window.location.reload()} className="mt-3 h-9 px-3 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700">Recargar</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function PlanWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [sector, setSector] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const creating = useRef(false);

  // Crear plan si es /planes/nuevo
  useEffect(() => {
    if (!id) {
      if (creating.current) return;
      creating.current = true;
      createPlan({}).then((p) => navigate(`/planes/${p.id}`, { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    getPlan(id).then((p) => { setPlan(p); setStep(p.paso_actual || 1); });
    getPlanesMeta().then(setMeta);
    listEmpresas().then(setEmpresas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (plan?.sector) getSector(plan.sector).then(setSector).catch(() => setSector(null));
    else setSector(null);
  }, [plan?.sector]);

  const save = useCallback(async (updates) => {
    if (!id) return;
    setSaving(true);
    try { const doc = await updatePlan(id, updates); setPlan((p) => ({ ...p, ...doc })); }
    catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  }, [id]);

  const reload = useCallback(() => { if (id) getPlan(id).then((p) => setPlan(p)); }, [id]);

  // merge local + persist
  const patch = (updates) => { setPlan((p) => ({ ...p, ...updates })); save(updates); };
  const goStep = (s) => { setStep(s); patch({ paso_actual: s }); };

  if (!plan || !meta) {
    return <Layout title="Plan de PRL"><div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div></Layout>;
  }

  return (
    <Layout
      title={plan.titulo || "Plan de PRL"}
      subtitle={sector ? sector.nombre : "Asistente de creación"}
      actions={
        <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="wizard-autosave">
          {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</> : <><Check className="w-3.5 h-3.5 text-blue-600" /> Guardado</>}
        </div>
      }
    >
      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-6">
        {STEPS.map((s) => {
          const Icon = s.icon;
          const active = step === s.key;
          const done = step > s.key;
          return (
            <button
              key={s.key}
              data-testid={`wizard-step-${s.key}`}
              onClick={() => goStep(s.key)}
              className={`flex items-center gap-2 h-10 px-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active ? "bg-blue-700 text-white" : done ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              <Icon className="w-4 h-4" /> <span className="hidden md:inline">{s.key}. {s.label}</span><span className="md:hidden">{s.key}</span>
            </button>
          );
        })}
      </div>

      {sector?.demo && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="sector-demo-aviso">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Contenido sectorial inicial <b>pendiente de validación técnica PREVELIS</b>. Revisa checklist, riesgos y medidas antes de entregar el Plan a un cliente.</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <StepErrorBoundary key={step}>
        {step === 1 && <StepEmpresa plan={plan} patch={patch} meta={meta} empresas={empresas} setEmpresas={setEmpresas} />}
        {step === 2 && <StepCentro plan={plan} patch={patch} />}
        {step === 3 && <StepPuestos plan={plan} patch={patch} sector={sector} />}
        {step === 4 && <StepVisita plan={plan} patch={patch} meta={meta} id={id} reload={reload} />}
        {step === 5 && <StepIA plan={plan} id={id} reload={reload} goStep={goStep} />}
        {step === 6 && <StepRevision plan={plan} patch={patch} meta={meta} />}
        {step === 7 && <StepGenerar plan={plan} id={id} reload={reload} />}
        {step === 8 && <StepIndividuales plan={plan} id={id} reload={reload} />}
        </StepErrorBoundary>
      </div>

      {/* Nav */}
      <div className="flex justify-between mt-6">
        <button
          onClick={() => goStep(Math.max(1, step - 1))}
          disabled={step === 1}
          data-testid="wizard-prev"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-slate-300 font-medium hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" /> Anterior
        </button>
        <button
          onClick={() => goStep(Math.min(8, step + 1))}
          disabled={step === 8}
          data-testid="wizard-next"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-40"
        >
          Siguiente <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </Layout>
  );
}

/* ------------------------------- Paso 1: Empresa ------------------------------- */
const EMPRESA_CAMPOS = [
  ["razon_social", "Razón social", true], ["nombre_comercial", "Nombre comercial"],
  ["nif", "CIF/NIF"], ["cnae", "CNAE / actividad"], ["actividad", "Actividad"],
  ["direccion", "Domicilio social"], ["representante", "Representante legal"],
  ["num_trabajadores", "Nº total de trabajadores"], ["telefono", "Teléfono"], ["email", "Email"],
];
const emptyEmp = () => ({ razon_social: "", nombre_comercial: "", nif: "", cnae: "", actividad: "", direccion: "", representante: "", num_trabajadores: "", telefono: "", email: "" });

function StepEmpresa({ plan, patch, meta, empresas, setEmpresas }) {
  const [mode, setMode] = useState("none"); // none | edit | create
  const [emp, setEmp] = useState(emptyEmp());
  const set = (k) => (e) => setEmp((f) => ({ ...f, [k]: e.target.value }));
  const mod = plan.modalidad || {};
  const vs = plan.vigilancia_salud || {};

  useEffect(() => {
    if (plan.empresa_id) {
      const e = empresas.find((x) => x.id === plan.empresa_id);
      if (e) { setEmp({ ...emptyEmp(), ...e }); setMode("edit"); }
    }
  }, [plan.empresa_id, empresas]);

  const seleccionar = (empresa_id) => {
    if (!empresa_id) { patch({ empresa_id: null }); setMode("none"); return; }
    const e = empresas.find((x) => x.id === empresa_id);
    patch({ empresa_id, titulo: e ? `Plan de PRL — ${e.razon_social}` : plan.titulo });
    if (e) { setEmp({ ...emptyEmp(), ...e }); setMode("edit"); }
  };

  const nueva = () => { setEmp(emptyEmp()); setMode("create"); patch({ empresa_id: null }); };

  const guardar = async () => {
    if (!emp.razon_social) return toast.error("La razón social es obligatoria");
    const payload = { ...emp, num_trabajadores: parseInt(emp.num_trabajadores || 0, 10) };
    if (mode === "create") {
      const creada = await createEmpresa(payload);
      const lst = await listEmpresas(); setEmpresas(lst);
      patch({ empresa_id: creada.id, titulo: `Plan de PRL — ${creada.razon_social}` });
      setMode("edit");
      toast.success("Empresa creada");
    } else {
      await updateEmpresa(plan.empresa_id, payload);
      const lst = await listEmpresas(); setEmpresas(lst);
      patch({ titulo: `Plan de PRL — ${emp.razon_social}` });
      toast.success("Empresa actualizada");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Empresa" required>
          <SelectInput
            testId="p1-empresa"
            value={mode === "create" ? "" : (plan.empresa_id || "")}
            onChange={(e) => seleccionar(e.target.value)}
            options={empresas.map((e) => ({ value: e.id, label: e.nif ? `${e.razon_social} (${e.nif})` : e.razon_social }))}
            placeholder="Seleccionar empresa existente…"
          />
        </Field>
        <Field label="Sector" required>
          <SelectInput
            testId="p1-sector"
            value={plan.sector || ""}
            onChange={(e) => patch({ sector: e.target.value })}
            groups={Object.entries(
              (meta.sectores || []).reduce((acc, s) => {
                const g = s.grupo || "Otros";
                (acc[g] = acc[g] || []).push({ value: s.clave, label: s.nombre });
                return acc;
              }, {})
            ).map(([label, options]) => ({ label, options }))}
          />
        </Field>
      </div>

      {mode === "none" && (
        <button onClick={nueva} data-testid="p1-nueva-empresa" className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800">
          <Plus className="w-4 h-4" /> Crear empresa nueva
        </button>
      )}

      {mode !== "none" && (
        <div className="border border-slate-200 rounded-lg p-5 space-y-4 bg-slate-50">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
              {mode === "create" ? <><Plus className="w-4 h-4" /> Nueva empresa</> : <><Pencil className="w-4 h-4" /> Datos de la empresa</>}
            </h4>
            <button onClick={nueva} className="text-xs font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Otra empresa
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {EMPRESA_CAMPOS.map(([k, label, req]) => (
              <Field key={k} label={label} required={req}>
                <TextInput testId={`p1-emp-${k}`} type={k === "num_trabajadores" ? "number" : "text"} value={emp[k] ?? ""} onChange={set(k)} />
              </Field>
            ))}
          </div>
          <button onClick={guardar} data-testid="p1-guardar-empresa" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">
            <Check className="w-4 h-4" /> {mode === "create" ? "Guardar empresa" : "Guardar cambios"}
          </button>
        </div>
      )}

      {/* Modalidad preventiva */}
      <div className="border-t border-slate-200 pt-5">
        <h4 className="font-heading font-semibold text-slate-800 mb-3">Modalidad preventiva</h4>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Modalidad" required>
            <SelectInput
              testId="p1-modalidad"
              value={mod.tipo || ""}
              onChange={(e) => patch({ modalidad: { ...mod, tipo: e.target.value } })}
              options={meta.modalidades.map((m) => ({ value: m.codigo, label: m.etiqueta }))}
            />
          </Field>
          {mod.tipo && (
            <Field label={mod.tipo === "empresario" ? "Persona que asume la prevención" : "Trabajador(es) designado(s)"}>
              <TextInput testId="p1-persona" defaultValue={mod.persona || ""} onBlur={(e) => patch({ modalidad: { ...mod, persona: e.target.value } })} />
            </Field>
          )}
        </div>
      </div>

      {/* Vigilancia de la salud */}
      <div className="border-t border-slate-200 pt-5">
        <h4 className="font-heading font-semibold text-slate-800 mb-1">Vigilancia de la salud</h4>
        <p className="text-xs text-slate-500 mb-3">Queda fuera del ámbito de PREVELIS. Cuando proceda, se contrata externamente con una entidad acreditada.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="¿Contratada externamente?">
            <SelectInput
              testId="p1-vs"
              value={vs.externa ? "si" : "no"}
              onChange={(e) => patch({ vigilancia_salud: { ...vs, externa: e.target.value === "si" } })}
              options={SI_NO_OPTS}
              placeholder=""
            />
          </Field>
          {vs.externa && (
            <Field label="Entidad acreditada">
              <TextInput testId="p1-vs-entidad" defaultValue={vs.entidad || ""} onBlur={(e) => patch({ vigilancia_salud: { ...vs, entidad: e.target.value } })} />
            </Field>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Paso 2: Centro ------------------------------- */
function StepCentro({ plan, patch }) {
  const c = plan.centro || {};
  const upd = (k) => (e) => patch({ centro: { ...plan.centro, [k]: e.target.value } });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="Nombre del centro" required><TextInput testId="p2-nombre" defaultValue={c.nombre || ""} onBlur={upd("nombre")} /></Field>
        <Field label="Dirección" required><TextInput testId="p2-direccion" defaultValue={c.direccion || ""} onBlur={upd("direccion")} /></Field>
        <Field label="Actividad desarrollada"><TextInput defaultValue={c.actividad || ""} onBlur={upd("actividad")} /></Field>
        <Field label="Nº de trabajadores"><TextInput type="number" defaultValue={c.num_trabajadores || ""} onBlur={upd("num_trabajadores")} /></Field>
        <Field label="Horario"><TextInput defaultValue={c.horario || ""} onBlur={upd("horario")} /></Field>
        <Field label="Superficie aprox. (m²)"><TextInput defaultValue={c.superficie || ""} onBlur={upd("superficie")} /></Field>
        <Field label="Nº de plantas"><TextInput defaultValue={c.plantas || ""} onBlur={upd("plantas")} /></Field>
      </div>
      <div className="grid gap-5">
        <Field label="Descripción general del centro"><TextArea testId="p2-descripcion" defaultValue={c.descripcion || ""} onBlur={upd("descripcion")} /></Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Instalaciones"><TextArea defaultValue={c.instalaciones || ""} onBlur={upd("instalaciones")} /></Field>
          <Field label="Equipos de trabajo"><TextArea defaultValue={c.equipos || ""} onBlur={upd("equipos")} /></Field>
          <Field label="Maquinaria"><TextArea defaultValue={c.maquinaria || ""} onBlur={upd("maquinaria")} /></Field>
          <Field label="Productos químicos (si existen)"><TextArea defaultValue={c.productos_quimicos || ""} onBlur={upd("productos_quimicos")} /></Field>
        </div>
        <Field label="Características relevantes"><TextArea defaultValue={c.caracteristicas || ""} onBlur={upd("caracteristicas")} /></Field>
      </div>
    </div>
  );
}

/* --------------------------- Paso 3: Puestos y trabajadores --------------------------- */
function StepPuestos({ plan, patch, sector }) {
  const puestos = plan.puestos || [];
  const trabajadores = plan.trabajadores || [];
  const YESNO = [{ value: "", label: "—" }, { value: "si", label: "Sí" }, { value: "no", label: "No" }];
  const PREGUNTAS = [
    ["mov_repetitivos", "¿Movimientos repetitivos?"],
    ["posturas_forzadas", "¿Posturas forzadas o mantenidas?"],
    ["manip_cargas", "¿Manipulación manual de cargas?"],
    ["empuje_arrastre", "¿Empuje o arrastre de cargas/equipos?"],
    ["pantallas", "¿Uso de pantallas de visualización?"],
    ["atencion_publico", "¿Atención habitual a público/pacientes/alumnos/clientes?"],
    ["trabajo_solitario", "¿Trabajo en solitario o aislado?"],
    ["desplazamientos", "¿Desplazamientos o conducción durante la jornada?"],
    ["tareas_limpieza", "¿Limpieza realizada por personal no específico de limpieza?"],
    ["tareas_mantenimiento", "¿Mantenimiento/reposición/recepción/carga-descarga por la plantilla?"],
    ["tareas_peligrosas", "¿Tareas especialmente peligrosas o poco frecuentes con procedimiento específico?"],
  ];

  const addPuesto = (nombre = "") => patch({ puestos: [...puestos, { id: uid(), nombre, num_trabajadores: "", tareas_habituales: "", tareas_ocasionales: "", zonas: "", equipos_maquinas_herramientas: "", productos_quimicos: "", epis: "", jornada_pie: "", jornada_sentado: "", jornada_caminando: "", jornada_conduciendo: "", mov_repetitivos: "", posturas_forzadas: "", manip_cargas: "", empuje_arrastre: "", pantallas: "", atencion_publico: "", trabajo_solitario: "", desplazamientos: "", tareas_limpieza: "", tareas_mantenimiento: "", tareas_peligrosas: "", tareas_peligrosas_desc: "" }] });
  const updPuesto = (pid, k, v) => patch({ puestos: puestos.map((p) => (p.id === pid ? { ...p, [k]: v } : p)) });
  const delPuesto = (pid) => patch({ puestos: puestos.filter((p) => p.id !== pid) });

  const addTrab = () => patch({ trabajadores: [...trabajadores, { id: uid(), nombre: "", puesto: "", departamento: "", responsable: "", fecha_incorporacion: "", observaciones: "" }] });
  const updTrab = (tid, k, v) => patch({ trabajadores: trabajadores.map((t) => (t.id === tid ? { ...t, [k]: v } : t)) });
  const delTrab = (tid) => patch({ trabajadores: trabajadores.filter((t) => t.id !== tid) });

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-slate-800">Puestos de trabajo</h4>
          <button onClick={() => addPuesto()} data-testid="p3-add-puesto" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir puesto</button>
        </div>
        {sector?.puestos_frecuentes?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-slate-400 self-center">Frecuentes:</span>
            {sector.puestos_frecuentes.map((n) => (
              <button key={n} onClick={() => addPuesto(n)} className="text-xs px-2.5 py-1 rounded-full border border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-700">+ {n}</button>
            ))}
          </div>
        )}
        {puestos.length === 0 ? (
          <p className="text-sm text-slate-400">Añade los puestos del centro. La IA propondrá los riesgos después.</p>
        ) : (
          <div className="space-y-4">
            {puestos.map((p) => (
              <div key={p.id} data-testid={`p3-puesto-${p.id}`} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input className="h-10 flex-1 border border-slate-300 rounded-lg px-3 font-medium text-slate-900 focus:outline-none focus:border-blue-600" placeholder="Nombre del puesto" defaultValue={p.nombre} onBlur={(e) => updPuesto(p.id, "nombre", e.target.value)} />
                  <div className="w-40"><TextInput type="number" testId={`p3-puesto-${p.id}-numtrab`} placeholder="Nº trabajadores" defaultValue={p.num_trabajadores} onBlur={(e) => updPuesto(p.id, "num_trabajadores", e.target.value)} /></div>
                  <button onClick={() => delPuesto(p.id)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Tareas habituales"><TextArea defaultValue={p.tareas_habituales} onBlur={(e) => updPuesto(p.id, "tareas_habituales", e.target.value)} /></Field>
                  <Field label="Tareas ocasionales / no rutinarias"><TextArea defaultValue={p.tareas_ocasionales} onBlur={(e) => updPuesto(p.id, "tareas_ocasionales", e.target.value)} /></Field>
                  <Field label="Zonas del centro donde trabaja"><TextInput defaultValue={p.zonas} onBlur={(e) => updPuesto(p.id, "zonas", e.target.value)} /></Field>
                  <Field label="Equipos, máquinas y herramientas"><TextInput defaultValue={p.equipos_maquinas_herramientas} onBlur={(e) => updPuesto(p.id, "equipos_maquinas_herramientas", e.target.value)} /></Field>
                  <Field label="Productos químicos o sustancias"><TextInput defaultValue={p.productos_quimicos} onBlur={(e) => updPuesto(p.id, "productos_quimicos", e.target.value)} /></Field>
                  <Field label="EPIs utilizados o necesarios"><TextInput defaultValue={p.epis} onBlur={(e) => updPuesto(p.id, "epis", e.target.value)} /></Field>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1.5">% aproximado de jornada (cuando proceda)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[["jornada_pie", "De pie"], ["jornada_sentado", "Sentado"], ["jornada_caminando", "Caminando"], ["jornada_conduciendo", "Conduciendo"]].map(([k, l]) => (
                      <Field key={k} label={l}><TextInput type="number" testId={`p3-puesto-${p.id}-${k}`} defaultValue={p[k]} onBlur={(e) => updPuesto(p.id, k, e.target.value)} /></Field>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PREGUNTAS.map(([k, l]) => (
                    <Field key={k} label={l}><SelectInput value={p[k] || ""} onChange={(e) => updPuesto(p.id, k, e.target.value)} options={YESNO} /></Field>
                  ))}
                </div>
                {p.tareas_peligrosas === "si" && (
                  <Field label="Describe la tarea peligrosa y su procedimiento específico">
                    <TextArea testId={`p3-puesto-${p.id}-peligrosas-desc`} defaultValue={p.tareas_peligrosas_desc} onBlur={(e) => updPuesto(p.id, "tareas_peligrosas_desc", e.target.value)} />
                  </Field>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-slate-200 pt-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-slate-800">Trabajadores</h4>
          <button onClick={addTrab} data-testid="p3-add-trab" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir trabajador</button>
        </div>
        {trabajadores.length === 0 ? (
          <p className="text-sm text-slate-400">Asigna trabajadores a los puestos creados.</p>
        ) : (
          <div className="space-y-3">
            {trabajadores.map((t) => (
              <div key={t.id} data-testid={`p3-trab-${t.id}`} className="border border-slate-200 rounded-lg p-3 space-y-3">
                <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                  <Field label="Nombre y apellidos"><TextInput defaultValue={t.nombre} onBlur={(e) => updTrab(t.id, "nombre", e.target.value)} /></Field>
                  <Field label="Puesto">
                    <SelectInput value={t.puesto} onChange={(e) => updTrab(t.id, "puesto", e.target.value)} options={puestos.map((p) => ({ value: p.nombre, label: p.nombre || "(sin nombre)" }))} />
                  </Field>
                  <button onClick={() => delTrab(t.id)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 mb-1"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Departamento (opcional)"><TextInput testId={`p3-trab-${t.id}-departamento`} defaultValue={t.departamento} onBlur={(e) => updTrab(t.id, "departamento", e.target.value)} /></Field>
                  <Field label="Responsable directo (opcional)"><TextInput testId={`p3-trab-${t.id}-responsable`} defaultValue={t.responsable} onBlur={(e) => updTrab(t.id, "responsable", e.target.value)} /></Field>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ------------------------------- Paso 4: Visita técnica ------------------------------- */
const ESP_LBL = { seguridad: "Seguridad", higiene: "Higiene", ergonomia: "Ergonomía" };

function PreanalisisResultado({ data, formId }) {
  const riesgos = data.riesgos_preliminares || [];
  const defs = data.posibles_deficiencias || [];
  const medidas = data.medidas_sugeridas || [];
  const puntos = data.puntos_verificar || [];
  return (
    <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-3" data-testid={`p4-preanalisis-res-${formId}`}>
      <div className="flex items-start gap-2 text-xs text-violet-800 bg-violet-100 rounded-lg px-2.5 py-1.5 mb-3">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span><b>Borrador preliminar</b> elaborado a partir de la información facilitada por el cliente. <b>Pendiente de validación técnica</b> — no modifica el expediente ni la revisión oficial.</span>
      </div>
      {data.resumen && <p className="text-sm text-slate-700 mb-3">{data.resumen}</p>}
      {riesgos.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Riesgos preliminares a confirmar</p>
          <div className="space-y-1.5">
            {riesgos.map((r, i) => (
              <div key={`${r.riesgo}-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                <span className="mt-0.5 text-xs px-1.5 py-0.5 rounded bg-white border border-violet-200 text-violet-700 shrink-0">{ESP_LBL[r.especialidad] || r.especialidad} · {r.nivel}</span>
                <span><b>{r.riesgo}</b>{r.motivo ? <span className="text-slate-500"> — {r.motivo}</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {defs.length > 0 && <PreList titulo="Posibles deficiencias" items={defs} />}
      {medidas.length > 0 && <PreList titulo="Medidas sugeridas (orientativas)" items={medidas} />}
      {puntos.length > 0 && <PreList titulo="Puntos a verificar en la visita" items={puntos} />}
    </div>
  );
}

function PreList({ titulo, items }) {
  return (
    <div className="mb-3 last:mb-0">
      <p className="text-xs font-semibold text-slate-600 mb-1.5">{titulo}</p>
      <ul className="list-disc pl-5 space-y-0.5">
        {items.map((x, i) => <li key={`${x}-${i}`} className="text-sm text-slate-700">{x}</li>)}
      </ul>
    </div>
  );
}

function FormulariosPanel({ plan, reloadPlan }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listarFormularios(plan.id).then((f) => { setForms(f); setLoading(false); }).catch(() => setLoading(false));
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const publicLink = (token) => `${window.location.origin}/f/${token}`;

  const crear = async () => {
    setBusy(true);
    try {
      const { token } = await crearFormulario(plan.id, "general");
      await load();
      try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace creado y copiado al portapapeles"); }
      catch { toast.success("Enlace creado"); }
    } catch { toast.error("No se pudo crear el formulario"); }
    finally { setBusy(false); }
  };

  const copiar = async (token) => {
    try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };

  const empresa = plan.empresa_nombre || (plan.centro || {}).nombre || "su centro";
  const msgWhatsapp = (token) => encodeURIComponent(`Hola, para preparar la visita de prevención de riesgos laborales de ${empresa}, por favor cumplimente este breve formulario: ${publicLink(token)}`);
  const asuntoMail = encodeURIComponent("Formulario previo a la visita de PRL");
  const cuerpoMail = (token) => encodeURIComponent(`Buenos días,\n\nPara preparar la visita de prevención de riesgos laborales, le agradeceríamos que cumplimente este breve formulario antes de nuestra visita:\n\n${publicLink(token)}\n\nGracias por su colaboración.`);

  const importar = async (formId) => {
    setBusy(true);
    try {
      const r = await importarFormulario(plan.id, formId);
      toast.success(`${r.respuestas_importadas} respuestas importadas. Revísalas en el checklist (marcadas "del cliente").`);
      await load();
      reloadPlan();
    } catch { toast.error("No se pudo importar el formulario"); }
    finally { setBusy(false); }
  };

  const borrar = async (formId) => {
    try { await eliminarFormulario(formId); await load(); toast.success("Formulario eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const [preAn, setPreAn] = useState({});
  const setPa = (fid, val) => setPreAn((s) => ({ ...s, [fid]: { ...(s[fid] || {}), ...val } }));

  const preanalizar = async (formId) => {
    setPa(formId, { estado: "procesando", resultado: null });
    try {
      await iniciarPreanalisis(plan.id, formId);
    } catch (e) {
      setPa(formId, { estado: "error", error: e?.response?.data?.detail || "No se pudo iniciar el pre-análisis" });
      return;
    }
    const espera = (ms) => new Promise((res) => setTimeout(res, ms));
    for (let i = 0; i < 120; i++) {
      await espera(3000);
      let est;
      try { est = await getPreanalisis(formId); } catch { continue; }
      if (est.estado === "completado") { setPa(formId, { estado: "completado", resultado: est.resultado }); toast.success("Pre-análisis completado"); return; }
      if (est.estado === "error") { setPa(formId, { estado: "error", error: est.error }); toast.error(est.error || "Error en el pre-análisis"); return; }
    }
    setPa(formId, { estado: "error", error: "El pre-análisis está tardando demasiado. Inténtalo de nuevo." });
  };

  // Hidrata resultados de pre-análisis ya generados (evita volver a gastar IA).
  useEffect(() => {
    forms.filter((f) => f.num_respuestas > 0).forEach((f) => {
      getPreanalisis(f.id).then((pa) => {
        if (pa.estado === "completado" && pa.resultado) setPa(f.id, { estado: "completado", resultado: pa.resultado });
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms]);

  return (
    <section className="border-t border-slate-200 pt-5" data-testid="p4-formularios">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-1">
        <div>
          <h4 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-700" /> Formularios externos para clientes</h4>
          <p className="text-xs text-slate-500 mt-0.5">Envía un enlace sin login para que el cliente aporte información, fotos y vídeo antes de la visita. Sus respuestas quedan pendientes de tu validación.</p>
        </div>
        <button onClick={crear} disabled={busy} data-testid="p4-form-crear" className="shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear enlace para el cliente
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 mt-3">Cargando…</p>
      ) : forms.length === 0 ? (
        <p className="text-sm text-slate-400 mt-3" data-testid="p4-form-vacio">Aún no has creado ningún formulario para este plan.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {forms.map((f) => {
            const est = ESTADO_FORM[f.estado] || ESTADO_FORM.enviado;
            const puedeImportar = ["completado", "en_progreso"].includes(f.estado) && !f.importado;
            return (
              <div key={f.id} data-testid={`p4-form-${f.id}`} className="border border-slate-200 rounded-xl p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-slate-800">{f.nombre}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${est.cls}`} data-testid={`p4-form-estado-${f.id}`}>{est.label}</span>
                  {f.importado && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Importado</span>}
                  <span className="ml-auto flex items-center gap-3 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><CheckSquare className="w-3.5 h-3.5" /> {f.num_respuestas}</span>
                    <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {f.num_fotos}</span>
                    {f.tiene_video && <span className="inline-flex items-center gap-1"><VideoIcon className="w-3.5 h-3.5" /> vídeo</span>}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <code className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-500 truncate max-w-[280px]">{publicLink(f.token)}</code>
                  <button onClick={() => copiar(f.token)} data-testid={`p4-form-copiar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Copy className="w-3.5 h-3.5" /> Copiar</button>
                  <a href={`https://wa.me/?text=${msgWhatsapp(f.token)}`} target="_blank" rel="noreferrer" data-testid={`p4-form-whatsapp-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-emerald-300 text-xs font-medium text-emerald-700 hover:bg-emerald-50"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                  <a href={`mailto:?subject=${asuntoMail}&body=${cuerpoMail(f.token)}`} data-testid={`p4-form-email-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Mail className="w-3.5 h-3.5" /> Email</a>
                  <a href={formularioPdfUrl(f.id)} target="_blank" rel="noreferrer" data-testid={`p4-form-pdf-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><DownloadCloud className="w-3.5 h-3.5" /> PDF</a>
                  {puedeImportar && (
                    <button onClick={() => importar(f.id)} disabled={busy} data-testid={`p4-form-importar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-blue-700 text-white text-xs font-medium hover:bg-blue-800 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Importar respuestas</button>
                  )}
                  {f.importado && (
                    <button onClick={() => importar(f.id)} disabled={busy} data-testid={`p4-form-reimportar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-blue-300 text-blue-700 text-xs font-medium hover:bg-blue-50 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Reimportar</button>
                  )}
                  <button onClick={() => borrar(f.id)} data-testid={`p4-form-borrar-${f.id}`} className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
                {f.num_respuestas > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <button onClick={() => preanalizar(f.id)} disabled={(preAn[f.id] || {}).estado === "procesando"} data-testid={`p4-form-preanalisis-${f.id}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50">
                      {(preAn[f.id] || {}).estado === "procesando" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando…</> : <><Sparkles className="w-3.5 h-3.5" /> Pre-análisis IA del formulario</>}
                    </button>
                    {(preAn[f.id] || {}).estado === "error" && <span className="ml-2 text-xs text-red-600">{preAn[f.id].error}</span>}
                    {(preAn[f.id] || {}).estado === "completado" && (preAn[f.id].resultado) && (
                      <PreanalisisResultado data={preAn[f.id].resultado} formId={f.id} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function StepVisita({ plan, patch, meta, id, reload }) {
  const v = plan.visita || {};
  const updV = (k) => (e) => patch({ visita: { ...plan.visita, [k]: e.target.value } });

  const setCheck = (cid, resp) => patch({ visita: { ...v, checklist: v.checklist.map((c) => (c.id === cid ? { ...c, respuesta: resp } : c)) } });
  const setCheckObs = (cid, obs) => patch({ visita: { ...v, checklist: v.checklist.map((c) => (c.id === cid ? { ...c, observacion: obs } : c)) } });
  // "Marcar sección como N.A." solo afecta a las preguntas de opción (Sí/No · Cumple/No cumple).
  const setSectionNA = (seccion) => patch({ visita: { ...v, checklist: v.checklist.map((c) => (c.seccion === seccion && (c.tipo === "si_no" || c.tipo === "cumple") ? { ...c, respuesta: "na" } : c)) } });

  // Respuestas del cliente importadas (pendientes de validación técnica)
  const acceptCliente = (cid) => patch({ visita: { ...v, checklist: v.checklist.map((c) => {
    if (c.id !== cid || !c.cliente) return c;
    let r = c.cliente.respuesta;
    if (c.tipo === "cumple") r = r === "si" ? "cumple" : r === "no" ? "no_cumple" : r;
    return { ...c, respuesta: r, observacion: c.cliente.observacion || c.observacion || "", cliente: null };
  }) } });
  const discardCliente = (cid) => patch({ visita: { ...v, checklist: v.checklist.map((c) => (c.id === cid ? { ...c, cliente: null } : c)) } });

  const addDef = () => patch({ visita: { ...v, deficiencias: [...(v.deficiencias || []), { id: uid(), descripcion: "", gravedad: "leve", fotos: [] }] } });
  const updDef = (did, k, val) => patch({ visita: { ...v, deficiencias: v.deficiencias.map((d) => (d.id === did ? { ...d, [k]: val } : d)) } });
  const delDef = (did) => patch({ visita: { ...v, deficiencias: v.deficiencias.filter((d) => d.id !== did) } });

  const uploadTo = async (file, ambito, ref, apply) => {
    try { const foto = await uploadPlanFoto(id, file, ambito, ref); apply(foto); toast.success("Foto añadida"); }
    catch { toast.error("No se pudo subir la foto"); }
  };
  const addFotoGeneral = (foto) => patch({ visita: { ...plan.visita, fotos: [...(plan.visita.fotos || []), foto] } });
  const delFotoGeneral = async (fid) => { await deletePlanFoto(id, fid); patch({ visita: { ...plan.visita, fotos: (plan.visita.fotos || []).filter((f) => f.id !== fid) } }); };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Fecha" required><TextInput testId="p4-fecha" type="date" defaultValue={v.fecha || ""} onBlur={updV("fecha")} /></Field>
        <Field label="Técnico" required><TextInput testId="p4-tecnico" defaultValue={v.tecnico || ""} onBlur={updV("tecnico")} /></Field>
        <Field label="Persona que atiende"><TextInput defaultValue={v.atiende || ""} onBlur={updV("atiende")} /></Field>
        <Field label="Áreas visitadas"><TextInput defaultValue={v.areas || ""} onBlur={updV("areas")} /></Field>
      </div>
      <Field label="Descripción general de la visita"><TextArea testId="p4-descripcion" defaultValue={v.descripcion_general || ""} onBlur={updV("descripcion_general")} /></Field>
      <Field label="Observaciones del técnico (texto libre)"><TextArea testId="p4-observaciones" defaultValue={v.observaciones_tecnico || ""} onBlur={updV("observaciones_tecnico")} /></Field>

      {/* Checklist Maestro */}
      <section className="border-t border-slate-200 pt-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
          <h4 className="font-heading font-semibold text-slate-800">Checklist Maestro de la visita</h4>
          {v.checklist && v.checklist.length > 0 && (() => {
            const byClave = {};
            v.checklist.forEach((c) => { if (c.clave) byClave[c.clave] = c; });
            const vis = v.checklist.filter((c) => !c.depende_de || (byClave[c.depende_de]?.respuesta === c.dep_valor));
            const total = vis.length;
            const resp = vis.filter(isAnswered).length;
            const pct = total ? Math.round((resp / total) * 100) : 0;
            const done = resp === total;
            return (
              <div className="flex items-center gap-3 min-w-[220px]" data-testid="p4-checklist-progreso">
                <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${pct}%` }} />
                </div>
                <span className={`text-xs font-semibold whitespace-nowrap ${done ? "text-emerald-600" : "text-slate-600"}`} data-testid="p4-checklist-contador">
                  {resp}/{total} respondidas
                </span>
              </div>
            );
          })()}
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Cada punto tiene su tipo de respuesta: <b>Cumple / No cumple</b> (de conformidad — "No cumple" genera una
          deficiencia que la IA convierte en riesgos y medidas), <b>Sí / No</b> (informativa — registra una circunstancia),
          o <b>campos de datos</b> (texto, número o lista). "No aplica" se ignora. Algunas preguntas se despliegan según la
          respuesta anterior y otras se marcan como <b>Requiere valoración técnica adicional</b>.
        </p>
        {(!v.checklist || v.checklist.length === 0) ? (
          <p className="text-sm text-slate-400">Cargando checklist…</p>
        ) : (
          <ChecklistMaestro checklist={v.checklist} setCheck={setCheck} setCheckObs={setCheckObs} setSectionNA={setSectionNA} acceptCliente={acceptCliente} discardCliente={discardCliente} />
        )}
        {v.checklist && v.checklist.length > 0 && <DeficienciasResumen checklist={v.checklist} />}
      </section>

      {/* Deficiencias */}
      <section className="border-t border-slate-200 pt-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-slate-800">Deficiencias observadas</h4>
          <button onClick={addDef} data-testid="p4-add-def" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir</button>
        </div>
        {(v.deficiencias || []).length === 0 ? (
          <p className="text-sm text-slate-400">Registra las deficiencias detectadas durante la visita.</p>
        ) : (
          <div className="space-y-3">
            {v.deficiencias.map((d) => (
              <div key={d.id} data-testid={`p4-def-${d.id}`} className="grid sm:grid-cols-[1fr_160px_auto] gap-3 items-end border border-slate-200 rounded-lg p-3">
                <Field label="Descripción"><TextInput defaultValue={d.descripcion} onBlur={(e) => updDef(d.id, "descripcion", e.target.value)} /></Field>
                <Field label="Gravedad"><SelectInput value={d.gravedad} onChange={(e) => updDef(d.id, "gravedad", e.target.value)} options={meta.gravedades.map((g) => ({ value: g.codigo, label: g.etiqueta }))} placeholder="" /></Field>
                <button onClick={() => delDef(d.id)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 mb-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Fotos generales */}
      <section className="border-t border-slate-200 pt-5">
        <h4 className="font-heading font-semibold text-slate-800 mb-3">Fotografías de la visita</h4>
        <div className="flex flex-wrap gap-3">
          {(v.fotos || []).map((f) => (
            <div key={f.id} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 group">
              <img src={img(f.url)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => delFotoGeneral(f.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <label data-testid="p4-add-foto" className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400 hover:text-blue-600">
            <ImagePlus className="w-5 h-5" /><span className="text-xs mt-1">Añadir</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) uploadTo(e.target.files[0], "visita", "", addFotoGeneral); e.target.value = ""; }} />
          </label>
        </div>
      </section>
    </div>
  );
}

const RESP_LBL = { si: "Sí", no: "No", cumple: "Cumple", no_cumple: "No cumple", na: "No aplica" };
const isDataTipo = (t) => t === "texto" || t === "numero" || t === "lista";
const isAnswered = (c) => {
  const r = c.respuesta;
  if (c.tipo === "lista") return Array.isArray(r) && r.some((x) => (x || "").trim());
  return r !== null && r !== undefined && r !== "";
};
const defValores = (c) => (c.def_valor && c.def_valor.length ? c.def_valor : (c.tipo === "cumple" ? ["no_cumple"] : []));
const isDeficiencia = (c) => {
  const r = c.respuesta;
  if (!r || r === "na" || isDataTipo(c.tipo)) return false;
  return defValores(c).includes(r);
};

function DeficienciasResumen({ checklist }) {
  const byClave = {};
  checklist.forEach((c) => { if (c.clave) byClave[c.clave] = c; });
  const isVisible = (c) => !c.depende_de || (byClave[c.depende_de]?.respuesta === c.dep_valor);
  const defs = checklist.filter((c) => isVisible(c) && isDeficiencia(c));
  const vals = checklist.filter((c) => isVisible(c) && c.valoracion_tecnica && c.respuesta && c.respuesta !== "na");
  return (
    <div className="mt-5 rounded-xl border border-slate-200 overflow-hidden" data-testid="p4-def-resumen">
      <div className={`px-4 py-2.5 flex items-center gap-2 ${defs.length ? "bg-red-50 border-b border-red-100" : "bg-emerald-50 border-b border-emerald-100"}`}>
        <AlertTriangle className={`w-4 h-4 ${defs.length ? "text-red-600" : "text-emerald-600"}`} />
        <span className={`text-sm font-semibold ${defs.length ? "text-red-700" : "text-emerald-700"}`} data-testid="p4-def-resumen-count">
          Deficiencias detectadas en el checklist ({defs.length})
        </span>
      </div>
      <div className="p-4 space-y-2">
        {defs.length === 0 ? (
          <p className="text-sm text-slate-500">No se han marcado deficiencias en el checklist. La IA evaluará igualmente los riesgos inherentes de la actividad.</p>
        ) : (
          <ul className="space-y-1.5">
            {defs.map((c) => (
              <li key={c.id} data-testid={`p4-def-item-${c.id}`} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                <span className="text-slate-700"><b className="text-slate-500 font-medium">{c.seccion_orden}. {c.seccion} —</b> {c.pregunta}{c.observacion ? <span className="text-slate-500 italic"> · {c.observacion}</span> : null}</span>
              </li>
            ))}
          </ul>
        )}
        {vals.length > 0 && (
          <div className="pt-2 mt-2 border-t border-slate-100" data-testid="p4-valtec-resumen">
            <p className="text-xs font-semibold text-amber-800 mb-1">Requieren valoración técnica adicional ({vals.length}):</p>
            <ul className="space-y-1">
              {vals.map((c) => (
                <li key={c.id} className="text-xs text-amber-700">• {c.seccion_orden}. {c.seccion} — {c.pregunta}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistMaestro({ checklist, setCheck, setCheckObs, setSectionNA, acceptCliente, discardCliente }) {
  const [expanded, setExpanded] = useState({});
  const byClave = {};
  checklist.forEach((c) => { if (c.clave) byClave[c.clave] = c; });
  const isVisible = (c) => !c.depende_de || (byClave[c.depende_de]?.respuesta === c.dep_valor);

  const secciones = [];
  const idx = {};
  for (const c of checklist) {
    const key = c.seccion_orden ?? c.seccion;
    if (idx[key] === undefined) {
      idx[key] = secciones.length;
      secciones.push({ orden: c.seccion_orden, titulo: c.seccion, items: [] });
    }
    secciones[idx[key]].items.push(c);
  }
  secciones.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

  return (
    <div className="space-y-2">
      {secciones.map((s) => (
        <ChecklistSection
          key={s.orden ?? s.titulo}
          seccion={s}
          isVisible={isVisible}
          open={!!expanded[s.orden]}
          toggle={() => setExpanded((e) => ({ ...e, [s.orden]: !e[s.orden] }))}
          setCheck={setCheck}
          setCheckObs={setCheckObs}
          setSectionNA={setSectionNA}
          acceptCliente={acceptCliente}
          discardCliente={discardCliente}
        />
      ))}
    </div>
  );
}

function ChecklistSection({ seccion, isVisible, open, toggle, setCheck, setCheckObs, setSectionNA, acceptCliente, discardCliente }) {
  const { orden, titulo, items } = seccion;
  const visibles = items.filter(isVisible);
  const total = visibles.length;
  const respondidas = visibles.filter(isAnswered).length;
  const opcionables = visibles.filter((c) => c.tipo === "si_no" || c.tipo === "cumple");
  const todasNA = opcionables.length > 0 && opcionables.every((c) => c.respuesta === "na");
  const completa = total > 0 && respondidas === total;
  const nDef = visibles.filter(isDeficiencia).length;
  const pendientesCliente = visibles.filter((c) => c.cliente).length;

  return (
    <div data-testid={`p4-seccion-${orden}`} className={`border rounded-xl overflow-hidden transition-colors ${todasNA ? "border-slate-200 bg-slate-50/60 opacity-70" : "border-slate-200"}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button data-testid={`p4-seccion-toggle-${orden}`} onClick={toggle} className="flex items-center gap-2 flex-1 text-left min-w-0">
          <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="font-medium text-sm text-slate-800 truncate">{orden}. {titulo}</span>
          <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${completa ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{respondidas}/{total}</span>
          {nDef > 0 && <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700" data-testid={`p4-seccion-def-${orden}`}>{nDef} deficiencia{nDef > 1 ? "s" : ""}</span>}
          {pendientesCliente > 0 && <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`p4-seccion-cliente-${orden}`}>{pendientesCliente} del cliente</span>}
        </button>
        <button
          data-testid={`p4-seccion-na-${orden}`}
          onClick={() => setSectionNA(titulo)}
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"
          title="Marcar las preguntas de opción de la sección como No Aplica"
        >
          <Minus className="w-3.5 h-3.5" /> Marcar sección como N.A.
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {visibles.map((c) => {
            const def = isDeficiencia(c);
            return (
            <div key={c.id} data-testid={`p4-check-${c.id}`} className={`border rounded-lg p-3 ${def ? "border-red-300 bg-red-50/60" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm text-slate-800">{c.pregunta}</p>
                {c.valoracion_tecnica && (
                  <span data-testid={`p4-valtec-${c.id}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    <AlertTriangle className="w-3 h-3" /> Valoración técnica
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {c.tipo === "cumple" && (<>
                  <RespBtn active={c.respuesta === "cumple"} onClick={() => setCheck(c.id, "cumple")} icon={Check} label="Cumple" cls="green" testId={`p4-cumple-${c.id}`} />
                  <RespBtn active={c.respuesta === "no_cumple"} onClick={() => setCheck(c.id, "no_cumple")} icon={X} label="No cumple" cls="red" testId={`p4-nocumple-${c.id}`} />
                  <RespBtn active={c.respuesta === "na"} onClick={() => setCheck(c.id, "na")} icon={Minus} label="No aplica" cls="slate" testId={`p4-na-${c.id}`} />
                </>)}
                {c.tipo === "si_no" && (<>
                  <RespBtn active={c.respuesta === "si"} onClick={() => setCheck(c.id, "si")} icon={Check} label="Sí" cls={def && c.respuesta === "si" ? "red" : "green"} testId={`p4-si-${c.id}`} />
                  <RespBtn active={c.respuesta === "no"} onClick={() => setCheck(c.id, "no")} icon={X} label="No" cls={def && c.respuesta === "no" ? "red" : "slate"} testId={`p4-no-${c.id}`} />
                  <RespBtn active={c.respuesta === "na"} onClick={() => setCheck(c.id, "na")} icon={Minus} label="No aplica" cls="slate" testId={`p4-na-${c.id}`} />
                </>)}
                {c.tipo === "numero" && (
                  <input type="number" data-testid={`p4-num-${c.id}`} className="h-9 w-40 border border-slate-300 rounded-lg px-3 text-sm focus:outline-none focus:border-blue-600" placeholder="Nº" defaultValue={c.respuesta ?? ""} onBlur={(e) => setCheck(c.id, e.target.value)} />
                )}
                {c.tipo === "texto" && (
                  <textarea data-testid={`p4-txt-${c.id}`} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" placeholder="Escribe la información…" defaultValue={c.respuesta ?? ""} onBlur={(e) => setCheck(c.id, e.target.value)} />
                )}
                {c.tipo === "lista" && (
                  <ListaInput value={c.respuesta} onCommit={(arr) => setCheck(c.id, arr)} testId={`p4-lista-${c.id}`} />
                )}
                {(c.tipo === "si_no" || c.tipo === "cumple") && (
                  <input className="h-9 flex-1 min-w-[160px] border border-slate-300 rounded-lg px-3 text-sm focus:outline-none focus:border-blue-600" placeholder="Observaciones (opcional)" defaultValue={c.observacion} onBlur={(e) => setCheckObs(c.id, e.target.value)} />
                )}
              </div>
              {c.cliente && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`p4-cliente-${c.id}`}>
                  <span className="text-xs font-semibold text-amber-800 shrink-0">Cliente — pendiente de validar:</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.cliente.respuesta === "si" ? "bg-emerald-600 text-white" : c.cliente.respuesta === "no" ? "bg-red-600 text-white" : "bg-slate-500 text-white"}`}>{RESP_LBL[c.cliente.respuesta] || c.cliente.respuesta}</span>
                  {c.cliente.observacion && <span className="text-xs text-amber-700 italic truncate max-w-[240px]" title={c.cliente.observacion}>"{c.cliente.observacion}"</span>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <button onClick={() => acceptCliente(c.id)} data-testid={`p4-cliente-aceptar-${c.id}`} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"><Check className="w-3.5 h-3.5" /> Aceptar</button>
                    <button onClick={() => discardCliente(c.id)} data-testid={`p4-cliente-descartar-${c.id}`} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-amber-300 text-amber-800 text-xs font-medium hover:bg-amber-100"><X className="w-3.5 h-3.5" /> Descartar</button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ListaInput({ value, onCommit, testId }) {
  const [items, setItems] = useState(() => (Array.isArray(value) ? value : []));
  const upd = (i, val) => setItems((p) => p.map((x, idx) => (idx === i ? val : x)));
  const add = () => setItems((p) => { const n = [...p, ""]; onCommit(n); return n; });
  const del = (i) => setItems((p) => { const n = p.filter((_, idx) => idx !== i); onCommit(n); return n; });
  return (
    <div className="space-y-2 w-full" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input className="h-9 flex-1 border border-slate-300 rounded-lg px-3 text-sm focus:outline-none focus:border-blue-600" placeholder={`Elemento ${i + 1}`} value={it} onChange={(e) => upd(i, e.target.value)} onBlur={() => onCommit(items)} data-testid={`${testId}-item-${i}`} />
          <button onClick={() => del(i)} data-testid={`${testId}-del-${i}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={add} data-testid={`${testId}-add`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Plus className="w-3.5 h-3.5" /> Añadir elemento</button>
    </div>
  );
}

function RespBtn({ active, onClick, icon: Icon, label, cls, testId }) {
  const on = { green: "bg-green-600 text-white border-green-600", red: "bg-red-600 text-white border-red-600", slate: "bg-slate-500 text-white border-slate-500" }[cls];
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border transition-colors ${active ? on : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ------------------------------- Paso 5: Análisis IA ------------------------------- */
function StepIA({ plan, id, reload, goStep }) {
  const [loading, setLoading] = useState(false);
  const prop = plan.propuesta_ia || {};
  const nRiesgos = (prop.riesgos || []).length;
  const puedeAnalizar = plan.sector && (plan.puestos || []).length > 0;

  const sondear = async () => {
    // El análisis corre en segundo plano (evita el timeout del proxy en producción).
    const espera = (ms) => new Promise((res) => setTimeout(res, ms));
    let intentos = 0;
    const MAX_INTENTOS = 120; // ~6 min (120 x 3s)
    while (intentos < MAX_INTENTOS) {
      await espera(3000);
      intentos += 1;
      let est;
      try {
        est = await getEstadoAnalisis(id);
      } catch {
        continue; // reintentar ante un fallo puntual de red
      }
      if (est.estado === "completado") {
        await reload();
        toast.success("Análisis completado");
        goStep(6);
        return;
      }
      if (est.estado === "error") {
        toast.error(est.error || "No se pudo completar el análisis IA");
        return;
      }
    }
    toast.error("El análisis está tardando más de lo esperado. Inténtalo de nuevo en unos minutos.");
  };

  // Reanuda el sondeo si el análisis quedó en curso (p. ej. tras recargar la página).
  useEffect(() => {
    if ((plan.analisis_ia || {}).estado === "procesando" && !loading) {
      setLoading(true);
      sondear().finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analizar = async () => {
    setLoading(true);
    try {
      await analizarPlan(id);
      await sondear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el análisis IA");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col items-center text-center py-10">
      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4"><Sparkles className="w-7 h-7 text-blue-700" /></div>
      <h3 className="font-heading font-semibold text-lg text-slate-800">Análisis con IA</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-lg">
        La IA analizará la empresa, el centro, los puestos, la visita, el checklist y las fotografías para
        proponer riesgos, medidas, EPIs, formación, acciones de planificación y medidas de emergencia.
        Después las revisarás y validarás.
      </p>
      {!puedeAnalizar && (
        <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> Selecciona un sector (Paso 1) y añade al menos un puesto (Paso 3).
        </div>
      )}
      {nRiesgos > 0 && (
        <div className="mt-4 text-sm text-slate-600">Ya existe una propuesta con <b>{nRiesgos}</b> riesgo(s). Puedes volver a analizar o continuar a la revisión.</div>
      )}
      <div className="flex gap-3 mt-6">
        <button
          onClick={analizar}
          disabled={loading || !puedeAnalizar}
          data-testid="p5-analizar"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-40"
        >
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analizando…</> : <><Sparkles className="w-5 h-5" /> {nRiesgos > 0 ? "Volver a analizar" : "Analizar con IA"}</>}
        </button>
        {nRiesgos > 0 && (
          <button onClick={() => goStep(6)} data-testid="p5-ir-revision" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-slate-300 font-medium hover:bg-slate-50">
            Ir a revisión <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Paso 6: Revisión técnica ------------------------------- */
const PRIOS = [{ value: "baja", label: "Baja" }, { value: "media", label: "Media" }, { value: "alta", label: "Alta" }];
const SI_NO_OPTS = [{ value: "si", label: "Sí" }, { value: "no", label: "No" }];
const AMBITO_OPTS = [{ value: "puesto", label: "Puesto" }, { value: "area", label: "Área" }, { value: "centro", label: "Centro" }];
const ESTADO_OPTS = [{ value: "controlado", label: "Controlado" }, { value: "desviacion", label: "Desviación" }, { value: "no_verificado", label: "No verificado" }];
const ESP_OPTS = [{ value: "seguridad", label: "Seguridad en el Trabajo" }, { value: "higiene", label: "Higiene Industrial" }, { value: "ergonomia", label: "Ergonomía y Psicosociología" }];
const SEG_ESTADO_OPTS = [{ value: "pendiente", label: "Pendiente" }, { value: "en_progreso", label: "En progreso" }, { value: "implantada", label: "Implantada" }];
const SEG_ESTADO_STYLE = { pendiente: "bg-red-100 text-red-700", en_progreso: "bg-amber-100 text-amber-700", implantada: "bg-emerald-100 text-emerald-700" };
const normSeg = (v) => (SEG_ESTADO_OPTS.some((o) => o.value === v) ? v : "pendiente");
const toISODate = (v) => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : "";
};
const EMER_CAMPOS = [
  ["organizacion", "Organización ante emergencias"], ["incendio", "Actuación ante incendio"], ["evacuacion", "Evacuación"],
  ["accidentes", "Accidentes y emergencias médicas"], ["primeros_auxilios", "Primeros auxilios"], ["comunicacion", "Comunicación con servicios externos"],
  ["medios_disponibles", "Medios disponibles"], ["medios_pendientes", "Medios pendientes de implantar"], ["salidas", "Salidas y vías de evacuación"],
  ["punto_reunion", "Punto de reunión"], ["formacion", "Información y formación necesaria"],
];
const REV_TABS = [
  ["riesgos", "Riesgos"], ["perfiles", "Perfiles de puesto"], ["planificacion", "Planificación"], ["emergencias", "Emergencias"],
  ["formacion", "Información y formación"], ["epis", "EPIs"],
];

function StepRevision({ plan, patch, meta }) {
  const [tab, setTab] = useState("riesgos");
  const rev = plan.revision || {};
  const niveles = meta.niveles_riesgo || [];
  const nivelInfo = (cod) => niveles.find((n) => n.codigo === cod) || {};
  const setRev = (next) => patch({ revision: next });
  const lines = (arr) => (arr || []).join("\n");
  const toArr = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const riesgos = rev.riesgos || [];
  const updR = (rid, obj) => setRev({ ...rev, riesgos: riesgos.map((r) => (r.id === rid ? { ...r, ...obj } : r)) });
  const delR = (rid) => setRev({ ...rev, riesgos: riesgos.filter((r) => r.id !== rid) });
  const addR = () => setRev({ ...rev, riesgos: [...riesgos, { id: uid(), codigo: "", ambito: "puesto", puesto: "", area: "", riesgo: "", descripcion: "", causa: "", nivel: "moderado", medidas: [], medidas_correctoras: [], estado_control: "controlado", valoracion_visita: "", epis: [], formacion: [], prioridad: "media", origen: "manual", aprobado: true }] });
  const aceptarTodo = () => setRev({ ...rev, riesgos: riesgos.map((r) => ({ ...r, aprobado: true })) });
  const grupos = {};
  riesgos.forEach((r) => {
    const k = r.ambito === "area" ? (r.area || "Área sin especificar") : r.ambito === "centro" ? "Centro de trabajo" : (r.puesto || "Puesto sin especificar");
    (grupos[k] = grupos[k] || []).push(r);
  });
  const aprobados = riesgos.filter((r) => r.aprobado).length;

  // Planificación
  const plani = rev.planificacion || [];
  const updP = (id, obj) => setRev({ ...rev, planificacion: plani.map((a) => (a.id === id ? { ...a, ...obj } : a)) });
  const delP = (id) => setRev({ ...rev, planificacion: plani.filter((a) => a.id !== id) });
  const addP = () => setRev({ ...rev, planificacion: [...plani, { id: uid(), riesgo_ref: "", accion: "", prioridad: "media", plazo: "", responsable: "La empresa designará responsable para la ejecución de la medida", estado: "pendiente", fecha_implantacion: "", evidencia: "", fotos: [], origen: "manual" }] });
  const addFotoP = async (aid, file) => {
    try {
      const foto = await uploadPlanFoto(plan.id, file, "seguimiento", aid);
      const a = plani.find((x) => x.id === aid);
      updP(aid, { fotos: [...(a.fotos || []), foto] });
      toast.success("Evidencia añadida");
    } catch { toast.error("No se pudo subir la evidencia"); }
  };
  const delFotoP = async (aid, fid) => {
    try { await deletePlanFoto(plan.id, fid); } catch (e) { console.error("Error al borrar evidencia (best-effort):", e); }
    const a = plani.find((x) => x.id === aid);
    updP(aid, { fotos: (a.fotos || []).filter((f) => f.id !== fid) });
  };

  // Perfiles preventivos por puesto (biblioteca personalizada; alimenta el documento individual)
  const perfiles = rev.perfiles_puesto || [];
  const updPerfil = (pid, obj) => setRev({ ...rev, perfiles_puesto: perfiles.map((p) => (p.id === pid ? { ...p, ...obj } : p)) });
  const addPerfil = () => setRev({ ...rev, perfiles_puesto: [...perfiles, { id: uid(), puesto: "", riesgos_inherentes: [], medidas_preventivas: [], epis: [], formacion: [], recomendaciones_salud: [] }] });
  const delPerfil = (pid) => setRev({ ...rev, perfiles_puesto: perfiles.filter((p) => p.id !== pid) });
  const updRi = (pid, rid, obj) => { const p = perfiles.find((x) => x.id === pid); updPerfil(pid, { riesgos_inherentes: (p.riesgos_inherentes || []).map((r) => (r.id === rid ? { ...r, ...obj } : r)) }); };
  const addRi = (pid) => { const p = perfiles.find((x) => x.id === pid); updPerfil(pid, { riesgos_inherentes: [...(p.riesgos_inherentes || []), { id: uid(), riesgo: "", explicacion: "", especialidad: "seguridad", nivel: "tolerable" }] }); };
  const delRi = (pid, rid) => { const p = perfiles.find((x) => x.id === pid); updPerfil(pid, { riesgos_inherentes: (p.riesgos_inherentes || []).filter((r) => r.id !== rid) }); };
  useEffect(() => {
    const needs = perfiles.some((p) => (p.riesgos_inherentes || []).some((r) => !r.id));
    if (needs) {
      setRev({ ...rev, perfiles_puesto: perfiles.map((p) => ({ ...p, riesgos_inherentes: (p.riesgos_inherentes || []).map((r) => (r.id ? r : { ...r, id: uid() })) })) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfiles]);

  // Emergencia (dict)
  const emer = rev.emergencia || {};
  const updEmer = (k, v) => setRev({ ...rev, emergencia: { ...emer, [k]: v } });

  // Info / formación / conclusiones
  const epis = rev.epis || { requiere: false, items: [], nota: "" };
  const setEpis = (obj) => setRev({ ...rev, epis: { ...epis, ...obj } });
  const addEpi = () => setEpis({ items: [...(epis.items || []), { id: uid(), epi: "", riesgo: "" }] });
  const updEpi = (eid, k, v) => setEpis({ items: epis.items.map((it) => (it.id === eid ? { ...it, [k]: v } : it)) });
  const delEpi = (eid) => setEpis({ items: epis.items.filter((it) => it.id !== eid) });
  useEffect(() => {
    const items = epis.items || [];
    if (items.some((it) => !it.id)) {
      setEpis({ items: items.map((it) => (it.id ? it : { ...it, id: uid() })) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epis.items]);

  if (!plan.propuesta_ia && riesgos.length === 0) {
    return <div className="text-center py-14 text-slate-500">Ejecuta primero el <b>Análisis IA</b> (Paso 5) para obtener propuestas que revisar.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 pb-px">
        {REV_TABS.map(([k, label]) => (
          <button key={k} data-testid={`p6-tab-${k}`} onClick={() => setTab(k)}
            className={`h-10 px-4 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "riesgos" && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">{aprobados} de {riesgos.length} aceptados · solo los aceptados pasarán al Plan.</p>
            <div className="flex gap-2">
              <button onClick={aceptarTodo} data-testid="p6-aceptar-todo" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-blue-200 text-blue-700 text-sm font-medium hover:bg-blue-50"><Check className="w-4 h-4" /> Aceptar todos</button>
              <button onClick={addR} data-testid="p6-add-riesgo" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir riesgo</button>
            </div>
          </div>
          {Object.entries(grupos).map(([grupo, rs]) => (
            <div key={grupo} className="space-y-3">
              <h5 className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500">{grupo}</h5>
              {rs.map((r) => {
                const ni = nivelInfo(r.nivel);
                return (
                  <div key={r.id} data-testid={`p6-riesgo-${r.id}`} className={`border rounded-lg p-4 space-y-3 ${r.aprobado ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <input className="w-full font-medium text-slate-900 bg-transparent focus:outline-none border-b border-transparent focus:border-blue-400" defaultValue={r.riesgo} placeholder="Riesgo" onBlur={(e) => updR(r.id, { riesgo: e.target.value })} />
                        <div className="flex items-center gap-2 mt-1">
                          {r.codigo && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.codigo}</span>}
                          {r.origen === "ia" && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">IA</span>}
                          {r.supuesto && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">supuesto</span>}
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: ni.color || "#64748B" }}>{ni.etiqueta || r.nivel}</span>
                        </div>
                      </div>
                      <button onClick={() => updR(r.id, { aprobado: !r.aprobado })} data-testid={`p6-aceptar-${r.id}`} className={`inline-flex items-center gap-1 h-8 px-3 rounded-lg text-sm font-medium border ${r.aprobado ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}><Check className="w-4 h-4" /> {r.aprobado ? "Aceptado" : "Aceptar"}</button>
                      <button onClick={() => delR(r.id)} data-testid={`p6-del-${r.id}`} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Field label="Descripción"><TextInput defaultValue={r.descripcion} onBlur={(e) => updR(r.id, { descripcion: e.target.value })} /></Field>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label={r.ambito === "area" ? "Área" : "Puesto"}><TextInput defaultValue={r.ambito === "area" ? r.area : r.puesto} onBlur={(e) => updR(r.id, r.ambito === "area" ? { area: e.target.value } : { puesto: e.target.value })} /></Field>
                        <Field label="Ámbito"><SelectInput value={r.ambito} onChange={(e) => updR(r.id, { ambito: e.target.value })} options={AMBITO_OPTS} placeholder="" /></Field>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="Nivel"><SelectInput value={r.nivel} onChange={(e) => updR(r.id, { nivel: e.target.value })} options={niveles.map((n) => ({ value: n.codigo, label: n.etiqueta }))} placeholder="" /></Field>
                        <Field label="Estado de control"><SelectInput value={r.estado_control || "controlado"} onChange={(e) => updR(r.id, { estado_control: e.target.value })} options={ESTADO_OPTS} placeholder="" /></Field>
                        <Field label="Prioridad"><SelectInput value={r.prioridad} onChange={(e) => updR(r.id, { prioridad: e.target.value })} options={PRIOS} placeholder="" /></Field>
                      </div>
                      <Field label="Valoración de la visita (estado observado)"><TextInput defaultValue={r.valoracion_visita} onBlur={(e) => updR(r.id, { valoracion_visita: e.target.value })} /></Field>
                      <Field label="Medidas preventivas previstas (una por línea)"><TextArea defaultValue={lines(r.medidas)} onBlur={(e) => updR(r.id, { medidas: toArr(e.target.value) })} /></Field>
                      {r.estado_control === "desviacion" && (
                        <Field label="Medidas correctoras — solo desviación (una por línea)"><TextArea defaultValue={lines(r.medidas_correctoras)} onBlur={(e) => updR(r.id, { medidas_correctoras: toArr(e.target.value) })} /></Field>
                      )}
                      <Field label="EPIs (una por línea)"><TextArea defaultValue={lines(r.epis)} onBlur={(e) => updR(r.id, { epis: toArr(e.target.value) })} /></Field>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {tab === "perfiles" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500 max-w-2xl">Perfil preventivo por puesto (guía completa personalizada por la IA). Alimenta el documento individual: <b>riesgos inherentes por especialidad</b>, medidas preventivas del puesto, formación, recomendaciones de salud y EPIs. La base procede de la biblioteca; edítala si es necesario.</p>
            <button onClick={addPerfil} data-testid="p6-add-perfil" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir puesto</button>
          </div>
          {perfiles.length === 0 ? (
            <p className="text-sm text-slate-400">No hay perfiles. Ejecuta el Análisis IA (Paso 5) para generarlos automáticamente desde la biblioteca, o añádelos manualmente.</p>
          ) : perfiles.map((p) => (
            <div key={p.id} data-testid={`p6-perfil-${p.id}`} className="border border-slate-200 rounded-lg p-4 space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1"><Field label="Puesto"><TextInput defaultValue={p.puesto} onBlur={(e) => updPerfil(p.id, { puesto: e.target.value })} /></Field></div>
                <button onClick={() => delPerfil(p.id)} data-testid={`p6-perfil-${p.id}-del`} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              {(() => {
                const pd = (plan.puestos || []).find((x) => (x.nombre || "").trim().toLowerCase() === (p.puesto || "").trim().toLowerCase());
                if (!pd) return null;
                const CL = { mov_repetitivos: "Mov. repetitivos", posturas_forzadas: "Posturas forzadas", manip_cargas: "Manip. cargas", empuje_arrastre: "Empuje/arrastre", pantallas: "Pantallas (PVD)", atencion_publico: "Atención al público", trabajo_solitario: "Trabajo en solitario", desplazamientos: "Desplazamientos/conducción", tareas_limpieza: "Limpieza no específica", tareas_mantenimiento: "Mantenimiento/carga-descarga", tareas_peligrosas: "Tareas peligrosas" };
                const conds = Object.entries(CL).filter(([k]) => (pd[k] || "").toLowerCase() === "si").map(([, l]) => l);
                const jornada = [["jornada_pie", "de pie"], ["jornada_sentado", "sentado"], ["jornada_caminando", "caminando"], ["jornada_conduciendo", "conduciendo"]].filter(([k]) => pd[k] && pd[k] !== "0").map(([k, l]) => `${l} ${pd[k]}%`).join(", ");
                const Row = (label, val) => val ? <div><span className="font-semibold text-slate-500">{label}:</span> {val}</div> : null;
                return (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1" data-testid={`p6-perfil-${p.id}-datos`}>
                    <div className="font-semibold text-slate-500 uppercase tracking-wide text-[10px] mb-1">Datos del puesto (visita técnica · Paso 3)</div>
                    {Row("Nº trabajadores", pd.num_trabajadores)}
                    {Row("Tareas habituales", pd.tareas_habituales)}
                    {Row("Tareas ocasionales", pd.tareas_ocasionales)}
                    {Row("Zonas", pd.zonas)}
                    {Row("Equipos/máquinas/herramientas", pd.equipos_maquinas_herramientas)}
                    {Row("Productos químicos", pd.productos_quimicos)}
                    {Row("EPIs", pd.epis)}
                    {Row("Jornada", jornada)}
                    {conds.length > 0 && Row("Condiciones (Sí)", conds.join(" · "))}
                  </div>
                );
              })()}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h6 className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500">Riesgos inherentes</h6>
                  <button onClick={() => addRi(p.id)} data-testid={`p6-perfil-${p.id}-add-ri`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-50"><Plus className="w-3.5 h-3.5" /> Riesgo</button>
                </div>
                {(p.riesgos_inherentes || []).length === 0 ? (
                  <p className="text-xs text-slate-400">Sin riesgos inherentes.</p>
                ) : (p.riesgos_inherentes || []).map((r, idx) => (
                  <div key={r.id || idx} className="grid sm:grid-cols-[1fr_1.3fr_150px_130px_auto] gap-2 items-end mb-2">
                    <Field label="Riesgo"><TextInput value={r.riesgo || ""} onChange={(e) => updRi(p.id, r.id, { riesgo: e.target.value })} /></Field>
                    <Field label="Explicación"><TextInput value={r.explicacion || ""} onChange={(e) => updRi(p.id, r.id, { explicacion: e.target.value })} /></Field>
                    <Field label="Especialidad"><SelectInput value={r.especialidad || "seguridad"} onChange={(e) => updRi(p.id, r.id, { especialidad: e.target.value })} options={ESP_OPTS} placeholder="" /></Field>
                    <Field label="Nivel"><SelectInput value={r.nivel} onChange={(e) => updRi(p.id, r.id, { nivel: e.target.value })} options={niveles.map((n) => ({ value: n.codigo, label: n.etiqueta }))} placeholder="" /></Field>
                    <button onClick={() => delRi(p.id, r.id)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 mb-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <Field label="Medidas preventivas del puesto (una por línea)"><TextArea defaultValue={lines(p.medidas_preventivas)} onBlur={(e) => updPerfil(p.id, { medidas_preventivas: toArr(e.target.value) })} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="EPIs del puesto (una por línea)"><TextArea defaultValue={lines(p.epis)} onBlur={(e) => updPerfil(p.id, { epis: toArr(e.target.value) })} /></Field>
                <Field label="Formación del puesto (una por línea)"><TextArea defaultValue={lines(p.formacion)} onBlur={(e) => updPerfil(p.id, { formacion: toArr(e.target.value) })} /></Field>
              </div>
              <Field label="Recomendaciones de salud laboral (una por línea)"><TextArea defaultValue={lines(p.recomendaciones_salud)} onBlur={(e) => updPerfil(p.id, { recomendaciones_salud: toArr(e.target.value) })} /></Field>
            </div>
          ))}
        </div>
      )}

      {tab === "planificacion" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="p6-seg-resumen">
              {SEG_ESTADO_OPTS.map((o) => {
                const n = plani.filter((a) => normSeg(a.estado) === o.value).length;
                return <span key={o.value} className={`px-2.5 py-1 rounded-full font-medium ${SEG_ESTADO_STYLE[o.value]}`}>{o.label}: {n}</span>;
              })}
            </div>
            <button onClick={addP} data-testid="p6-add-accion" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir acción</button>
          </div>
          {plani.length === 0 ? <p className="text-sm text-slate-400">Sin acciones de planificación.</p> : plani.map((a) => (
            <div key={a.id} data-testid={`p6-accion-${a.id}`} className="border border-slate-200 rounded-lg p-3 grid sm:grid-cols-2 gap-3">
              <Field label="Riesgo / deficiencia"><TextInput defaultValue={a.riesgo_ref} onBlur={(e) => updP(a.id, { riesgo_ref: e.target.value })} /></Field>
              <Field label="Acción preventiva"><TextInput defaultValue={a.accion} onBlur={(e) => updP(a.id, { accion: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prioridad"><SelectInput value={a.prioridad} onChange={(e) => updP(a.id, { prioridad: e.target.value })} options={PRIOS} placeholder="" /></Field>
                <Field label="Plazo"><TextInput defaultValue={a.plazo} onBlur={(e) => updP(a.id, { plazo: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                <Field label="Responsable"><TextInput defaultValue={a.responsable} onBlur={(e) => updP(a.id, { responsable: e.target.value })} /></Field>
                <button onClick={() => delP(a.id)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 mb-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              {/* Seguimiento de implantación */}
              <div className="sm:col-span-2 border-t border-dashed border-slate-200 pt-3 mt-1">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Seguimiento de implantación</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Estado de implantación">
                    <SelectInput testId={`p6-accion-${a.id}-estado`} value={normSeg(a.estado)} onChange={(e) => updP(a.id, { estado: e.target.value })} options={SEG_ESTADO_OPTS} placeholder="" />
                  </Field>
                  <Field label="Fecha de implantación / actualización">
                    <input type="date" data-testid={`p6-accion-${a.id}-fecha`} value={toISODate(a.fecha_implantacion)} onChange={(e) => updP(a.id, { fecha_implantacion: e.target.value })} className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" />
                  </Field>
                </div>
                <div className="mt-3">
                  <Field label="Evidencia / observaciones de seguimiento">
                    <TextArea testId={`p6-accion-${a.id}-evidencia`} defaultValue={a.evidencia} onBlur={(e) => updP(a.id, { evidencia: e.target.value })} placeholder="Ej.: instalado extintor de CO2 junto al cuadro eléctrico; formación impartida el 12/06." />
                  </Field>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-2">Evidencia fotográfica (opcional)</p>
                  <div className="flex flex-wrap gap-3">
                    {(a.fotos || []).map((f) => (
                      <div key={f.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 group">
                        <img src={img(f.url)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => delFotoP(a.id, f.id)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <label data-testid={`p6-accion-${a.id}-add-foto`} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400 hover:text-blue-600">
                      <ImagePlus className="w-4 h-4" /><span className="text-[10px] mt-1">Añadir</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) addFotoP(a.id, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "emergencias" && (
        <div className="grid sm:grid-cols-2 gap-4">
          {EMER_CAMPOS.map(([k, label]) => (
            <Field key={k} label={label}><TextArea data-testid={`p6-emer-${k}`} defaultValue={emer[k] || ""} onBlur={(e) => updEmer(k, e.target.value)} /></Field>
          ))}
        </div>
      )}

      {tab === "formacion" && (
        <div className="space-y-5">
          <Field label="Información a los trabajadores (una por línea)"><TextArea data-testid="p6-informacion" defaultValue={lines(rev.informacion)} onBlur={(e) => setRev({ ...rev, informacion: toArr(e.target.value) })} /></Field>
          <Field label="Formación preventiva necesaria (una por línea)"><TextArea data-testid="p6-formacion" defaultValue={lines(rev.formacion)} onBlur={(e) => setRev({ ...rev, formacion: toArr(e.target.value) })} /></Field>
          <Field label="Conclusiones técnicas"><TextArea data-testid="p6-conclusiones" defaultValue={rev.conclusiones || ""} onBlur={(e) => setRev({ ...rev, conclusiones: e.target.value })} /></Field>
        </div>
      )}

      {tab === "epis" && (
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="p6-epis-requiere" checked={!!epis.requiere} onChange={(e) => setEpis({ requiere: e.target.checked })} className="w-4 h-4 accent-blue-700" />
            <span className="text-sm text-slate-700">Se requieren EPIs específicos</span>
          </label>
          {epis.requiere && (
            <div className="space-y-2">
              {(epis.items || []).map((it) => (
                <div key={it.id} data-testid={`p6-epi-${it.id}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end border border-slate-200 rounded-lg p-3">
                  <Field label="EPI"><TextInput testId={`p6-epi-${it.id}-nombre`} value={it.epi || ""} onChange={(e) => updEpi(it.id, "epi", e.target.value)} /></Field>
                  <Field label="Riesgo frente al que protege"><TextInput testId={`p6-epi-${it.id}-riesgo`} value={it.riesgo || ""} onChange={(e) => updEpi(it.id, "riesgo", e.target.value)} /></Field>
                  <button onClick={() => delEpi(it.id)} data-testid={`p6-epi-${it.id}-del`} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 mb-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addEpi} data-testid="p6-add-epi" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800"><Plus className="w-4 h-4" /> Añadir EPI</button>
            </div>
          )}
          {!epis.requiere && <p className="text-sm text-slate-500">Se indicará en el Plan que no se identifican necesidades específicas de EPIs, sin perjuicio de futuras revisiones.</p>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Paso 7: Generar Plan ------------------------------- */
function StepGenerar({ plan, id, reload }) {
  const [loading, setLoading] = useState(false);
  const rev = plan.revision || {};
  const nAprob = (rev.riesgos || []).filter((r) => r.aprobado).length;
  const nAcc = (rev.planificacion || []).length;
  const nEm = Object.values(rev.emergencia || {}).filter((v) => (v || "").trim()).length;
  const doc = plan.documento;
  const informes = plan.informes_visita || [];
  const puede = nAprob > 0;
  const [loadingInf, setLoadingInf] = useState(false);
  const checklist = (plan.visita || {}).checklist || [];
  const byClaveG = {};
  checklist.forEach((c) => { if (c.clave) byClaveG[c.clave] = c; });
  const visG = checklist.filter((c) => !c.depende_de || (byClaveG[c.depende_de]?.respuesta === c.dep_valor));
  const clTotal = visG.length;
  const clResp = visG.filter(isAnswered).length;
  const clPend = clTotal - clResp;

  const generar = async () => {
    setLoading(true);
    try {
      await generarPlan(id);
      await reload();
      toast.success("Plan de PRL generado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el Plan");
    } finally { setLoading(false); }
  };

  const [delLoadingDoc, setDelLoadingDoc] = useState(false);
  const borrarPlan = async () => {
    if (!window.confirm("¿Borrar el Plan de PRL generado? Podrás volver a generarlo cuando quieras.")) return;
    setDelLoadingDoc(true);
    try {
      await eliminarDocumentoPlan(id);
      await reload();
      toast.success("Plan de PRL borrado. Ya puedes generar uno nuevo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar el Plan");
    } finally { setDelLoadingDoc(false); }
  };


  const generarInforme = async () => {
    if (clPend > 0) {
      const ok = window.confirm(
        `Quedan ${clPend} de ${clTotal} puntos del checklist sin responder.\n\n` +
        `El Informe de Visita solo incluirá los puntos verificados. Los puntos sin responder NO aparecerán.\n\n` +
        `¿Quieres generar el informe de todos modos?`
      );
      if (!ok) return;
    }
    setLoadingInf(true);
    try {
      await generarInformeVisita(id);
      await reload();
      toast.success("Informe de Visita Técnica generado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el Informe de Visita");
    } finally { setLoadingInf(false); }
  };

  const borrarInforme = async (informeId) => {
    try {
      await eliminarInformeVisita(id, informeId);
      await reload();
      toast.success("Informe eliminado del historial");
    } catch {
      toast.error("No se pudo eliminar el informe");
    }
  };

  return (
    <div className="flex flex-col items-center text-center py-10">
      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4"><FileDown className="w-7 h-7 text-blue-700" /></div>
      <h3 className="font-heading font-semibold text-lg text-slate-800">Generar Plan de PRL completo</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-lg">
        Se generará un único documento en Word y PDF con índice automático, incluyendo solo el
        contenido aprobado en la Revisión técnica.
      </p>
      <div className="flex flex-wrap justify-center gap-3 mt-5 text-sm">
        <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700"><b>{nAprob}</b> riesgos aceptados</span>
        <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700"><b>{nAcc}</b> acciones</span>
        <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700"><b>{nEm}</b> medidas de emergencia</span>
      </div>
      {!puede && (
        <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> Acepta al menos un riesgo en la Revisión técnica (Paso 6).
        </div>
      )}
      <button
        onClick={generar}
        disabled={loading || !puede}
        data-testid="p7-generar"
        className="inline-flex items-center gap-2 h-13 px-7 mt-6 rounded-xl bg-blue-700 text-white font-semibold text-base hover:bg-blue-800 disabled:opacity-40 py-3"
      >
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando… (puede tardar)</> : <><FileDown className="w-5 h-5" /> {doc ? "Volver a generar" : "Generar Plan de PRL completo"}</>}
      </button>

      {doc && doc.doc_uid && (
        <div className="mt-8 w-full max-w-md" data-testid="p7-resultado">
          <div className="text-xs text-slate-400 mb-2">Documento {doc.doc_uid} · v{doc.version}</div>
          <div className="grid grid-cols-2 gap-3">
            <a href={`${BACKEND}${doc.docx_url}`} target="_blank" rel="noreferrer" data-testid="p7-docx" className="inline-flex items-center justify-center gap-2 h-12 rounded-xl border border-slate-300 font-medium hover:bg-slate-50">
              <FileText className="w-5 h-5 text-blue-700" /> Word
            </a>
            <a href={`${BACKEND}${doc.pdf_url}`} target="_blank" rel="noreferrer" data-testid="p7-pdf" className="inline-flex items-center justify-center gap-2 h-12 rounded-xl border border-slate-300 font-medium hover:bg-slate-50">
              <FileText className="w-5 h-5 text-red-600" /> PDF
            </a>
          </div>
          <a href={`${BACKEND}/api/planes/${id}/paquete-completo/zip`} data-testid="p7-paquete-zip"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 h-12 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 font-medium hover:bg-blue-100">
            <FileDown className="w-5 h-5" /> Descargar paquete completo (ZIP)
          </a>
          <p className="mt-2 text-xs text-slate-400">Incluye el Plan Maestro, los documentos individuales, los informes de visita y los registros de formación generados.</p>
          <button
            onClick={borrarPlan}
            disabled={delLoadingDoc}
            data-testid="p7-borrar-plan"
            title="Borrar el Plan generado para generar uno nuevo desde cero"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 h-11 rounded-xl border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-40"
          >
            {delLoadingDoc ? <><Loader2 className="w-4 h-4 animate-spin" /> Borrando…</> : <><Trash2 className="w-4 h-4" /> Borrar Plan generado</>}
          </button>
        </div>
      )}

      {/* Informe de Visita Técnica — documento independiente */}
      <div className="mt-10 w-full max-w-lg border-t border-slate-200 pt-8" data-testid="p7-informe-visita">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3"><ClipboardCheck className="w-6 h-6 text-emerald-700" /></div>
        <h3 className="font-heading font-semibold text-lg text-slate-800">Informe de Visita Técnica</h3>
        <p className="text-sm text-slate-500 mt-2">
          Documento independiente del Plan con los datos de la visita, observaciones, incumplimientos detectados y
          reportaje fotográfico. Un mismo plan puede tener varias visitas a lo largo del tiempo.
        </p>
        {clTotal > 0 && (
          clPend > 0 ? (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-md mx-auto" data-testid="p7-checklist-aviso">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Quedan <b>{clPend}</b> de {clTotal} puntos del checklist sin responder (Paso 4). Solo se incluirán los verificados.</span>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-w-md mx-auto" data-testid="p7-checklist-ok">
              <Check className="w-4 h-4 shrink-0" /> Checklist completo: {clResp}/{clTotal} puntos respondidos.
            </div>
          )
        )}
        <button
          onClick={generarInforme}
          disabled={loadingInf}
          data-testid="p7-generar-informe"
          className="inline-flex items-center gap-2 h-12 px-6 mt-5 rounded-xl bg-emerald-700 text-white font-semibold hover:bg-emerald-800 disabled:opacity-40"
        >
          {loadingInf ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando…</> : <><FileDown className="w-5 h-5" /> {informes.length ? "Generar nuevo informe" : "Generar Informe de Visita"}</>}
        </button>
        {informes.length > 0 && (
          <div className="mt-6 w-full max-w-xl mx-auto text-left" data-testid="p7-informe-historial">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Historial de visitas ({informes.length})</div>
            <div className="space-y-2">
              {informes.map((it) => (
                <div key={it.id} data-testid={`p7-inf-${it.id}`} className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-3">
                  <ClipboardCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">Visita del {it.fecha_visita || "—"}</div>
                    <div className="text-xs text-slate-400">Informe {it.doc_uid} · generado {(it.generado_at || "").slice(0, 10)}</div>
                  </div>
                  <a href={`${BACKEND}${it.docx_url}`} target="_blank" rel="noreferrer" data-testid={`p7-inf-docx-${it.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                    <FileText className="w-4 h-4 text-blue-700" /> Word
                  </a>
                  <a href={`${BACKEND}${it.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`p7-inf-pdf-${it.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                    <FileText className="w-4 h-4 text-red-600" /> PDF
                  </a>
                  <button onClick={() => borrarInforme(it.id)} data-testid={`p7-inf-del-${it.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Eliminar del historial">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ------------------------- Paso 8: Documentos individuales ------------------------- */
function StepIndividuales({ plan, id, reload }) {
  const [loading, setLoading] = useState(false);
  const [recLoading, setRecLoading] = useState(false);
  const [formLoading, setFormLoading] = useState({});
  const [delLoading, setDelLoading] = useState({});
  const [allDelLoading, setAllDelLoading] = useState(false);
  const [formDur, setFormDur] = useState({});
  const [batchDur, setBatchDur] = useState("");
  const navigate = useNavigate();
  const trabajadores = plan.trabajadores || [];
  const docs = plan.documentos_individuales || [];
  const nAprob = ((plan.revision || {}).riesgos || []).filter((r) => r.aprobado).length;
  const generado = plan.estado === "generado" || !!(plan.documento || {}).pdf_url;
  const puede = generado && nAprob > 0 && trabajadores.length > 0;

  const docFor = (t) => docs.find((d) => d.trabajador_id === t.id);
  const fdt = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const hayDocs = docs.length > 0 || (plan.formaciones_trabajador || []).length > 0;
  const borrarTodos = async () => {
    if (!window.confirm("¿Borrar los documentos generados de TODOS los trabajadores? Podrás regenerarlos después.")) return;
    setAllDelLoading(true);
    try {
      await borrarTodosDocsTrabajadores(id);
      await reload();
      toast.success("Documentos de todos los trabajadores borrados. Ya puedes regenerarlos.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron borrar los documentos");
    } finally { setAllDelLoading(false); }
  };

  const generar = async () => {
    setLoading(true);
    try {
      const res = await generarDocumentosIndividuales(id);
      await reload();
      const errs = (res && res.errores) || [];
      if (errs.length) {
        toast.warning(`${(res.documentos || []).length} generado(s), ${errs.length} con error: ${errs.map((e) => e.nombre).join(", ")}`);
      } else {
        toast.success("Documentos individuales generados");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron generar los documentos");
    } finally { setLoading(false); }
  };

  const generarRec = async () => {
    if (!plan.empresa_id) { toast.error("Vincula una empresa al plan (Paso 1) antes de generar recordatorios"); return; }
    setRecLoading(true);
    try {
      const res = await generarRecordatoriosDesdePlan(id);
      const sinDatos = res.trabajadores_sin_datos || [];
      if (res.creados > 0) {
        toast.success(`${res.creados} recordatorio(s) creado(s)`, {
          action: { label: "Ver recordatorios", onClick: () => navigate(`/recordatorios/${res.empresa_id}`) },
        });
      } else {
        toast.info("No hay recordatorios nuevos que crear (ya estaban generados).");
      }
      if (sinDatos.length) toast.warning(`Sin EPIs/formación de puesto: ${sinDatos.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron generar los recordatorios");
    } finally { setRecLoading(false); }
  };

  const formFor = (t) => (plan.formaciones_trabajador || []).find((f) => f.trabajador_id === t.id);
  const generarForm = async (t) => {
    setFormLoading((s) => ({ ...s, [t.id]: true }));
    try {
      const f = await generarFormacionTrabajador(id, t.id, formDur[t.id] ? Number(formDur[t.id]) : undefined);
      await reload();
      if (f.revision_pendiente) toast.warning(`Formación de ${t.nombre || "trabajador"} generada — hay apartados marcados para revisión técnica`);
      else toast.success(`Formación generada para ${t.nombre || "trabajador"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la formación");
    } finally { setFormLoading((s) => ({ ...s, [t.id]: false })); }
  };

  const borrarDocs = async (t) => {
    setDelLoading((s) => ({ ...s, [t.id]: true }));
    try {
      await borrarDocsTrabajador(id, t.id);
      await reload();
      toast.success(`Documentos de ${t.nombre || "trabajador"} borrados. Ya puedes regenerarlos.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron borrar los documentos");
    } finally { setDelLoading((s) => ({ ...s, [t.id]: false })); }
  };

  const [batch, setBatch] = useState(
    (plan.formacion_batch || {}).estado === "en_progreso" ? plan.formacion_batch : null
  );
  const batchRunning = batch?.estado === "en_progreso";
  useEffect(() => {
    if (!batchRunning) return;
    const iv = setInterval(async () => {
      try {
        const b = await getFormacionBatch(id);
        setBatch(b);
        if (b?.estado !== "en_progreso") {
          clearInterval(iv);
          await reload();
          const errs = (b?.errores || []).length;
          if (errs) toast.warning(`Formación generada. ${errs} trabajador(es) con error.`);
          else toast.success("Formación generada para todos los trabajadores");
        }
      } catch { /* reintenta en el siguiente tick */ }
    }, 2500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRunning, id]);

  const startBatch = async () => {
    try {
      const r = await generarFormacionBatch(id, batchDur ? Number(batchDur) : undefined);
      if (r.iniciado) setBatch({ estado: "en_progreso", total: r.total, hechos: 0, actual: "", errores: [] });
      else toast.info(r.detail || "Ya hay una generación en curso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la generación");
    }
  };

  return (
    <div data-testid="p8-individuales">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading font-semibold text-lg text-slate-800">Documento de información preventiva del puesto</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Para cada trabajador se genera un documento personalizado (Word y PDF) de información preventiva de su puesto
            (arts. 18 y 19): descripción del puesto, riesgos inherentes, riesgos específicos de la visita, medidas
            preventivas, normas de seguridad, actuación en emergencias, EPIs y formación, con justificante de entrega.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {hayDocs && (
            <button
              onClick={borrarTodos}
              disabled={allDelLoading}
              data-testid="p8-borrar-todos"
              title="Borrar los documentos de todos los trabajadores antes de regenerarlos"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl border border-red-300 text-red-600 font-semibold hover:bg-red-50 disabled:opacity-40"
            >
              {allDelLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Borrando…</> : <><Trash2 className="w-5 h-5" /> Borrar todos</>}
            </button>
          )}
          {docs.length > 0 && (
            <a
              href={`${BACKEND}/api/planes/${id}/documentos-individuales/zip`}
              data-testid="p8-zip"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl border border-blue-700 text-blue-700 font-semibold hover:bg-blue-50"
            >
              <Download className="w-5 h-5" /> Descargar todo (ZIP)
            </a>
          )}
          <button
            onClick={generar}
            disabled={loading || !puede}
            data-testid="p8-generar"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-40"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando…</> : <><IdCard className="w-5 h-5" /> {docs.length ? "Volver a generar" : "Generar documentos"}</>}
          </button>
        </div>
      </div>

      {/* Recordatorios de seguimiento a partir de EPIs y formación del plan */}
      {puede && (
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="p8-recordatorios-bloque">
          <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-semibold text-slate-900">Recordatorios de seguimiento</div>
            <p className="text-sm text-slate-500">
              Crea automáticamente los vencimientos de EPIs y formación de cada trabajador (según su puesto),
              con caducidad a 12 meses editable. No duplica los que ya existan.
            </p>
          </div>
          <button
            onClick={generarRec}
            disabled={recLoading}
            data-testid="p8-generar-recordatorios"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-purple-600 text-purple-700 font-semibold hover:bg-purple-50 disabled:opacity-40 shrink-0"
          >
            {recLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando…</> : <><BellRing className="w-5 h-5" /> Generar recordatorios</>}
          </button>
        </div>
      )}

      {/* Generación masiva de la formación (IA) en segundo plano */}
      {puede && (
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4" data-testid="p8-form-batch-bloque">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-semibold text-slate-900">Formación e información preventiva (IA) — todos los trabajadores</div>
              <p className="text-sm text-slate-500">
                Genera de una vez el documento de formación de cada trabajador. Se ejecuta en segundo plano; puedes seguir el progreso aquí.
              </p>
            </div>
            <div className="flex gap-2 shrink-0 items-center">
              <input
                type="number" min="1" value={batchDur}
                onChange={(e) => setBatchDur(e.target.value)}
                placeholder="min (auto)"
                data-testid="p8-form-batch-dur"
                title="Duración en minutos para todos (vacío = cálculo automático)"
                className="w-28 h-11 px-3 rounded-xl border border-slate-300 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
              />
              {(plan.formaciones_trabajador || []).length > 0 && (
                <a
                  href={`${BACKEND}/api/planes/${id}/formacion/zip`}
                  data-testid="p8-form-zip"
                  className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-purple-600 text-purple-700 font-semibold hover:bg-purple-50"
                >
                  <Download className="w-5 h-5" /> ZIP
                </a>
              )}
              <button
                onClick={startBatch}
                disabled={batchRunning}
                data-testid="p8-form-batch"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-purple-700 text-white font-semibold hover:bg-purple-800 disabled:opacity-40"
              >
                {batchRunning ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando…</> : <><Sparkles className="w-5 h-5" /> Generar todos (IA)</>}
              </button>
            </div>
          </div>
          {batch && (
            <div className="mt-4" data-testid="p8-form-batch-progress">
              <div className="flex items-center justify-between text-sm text-slate-600 mb-1">
                <span>{batchRunning ? (batch.actual ? `Generando: ${batch.actual}` : "Iniciando…") : "Completado"}</span>
                <span data-testid="p8-form-batch-count">{batch.hechos || 0} / {batch.total || 0}</span>
              </div>
              <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-purple-600 transition-all duration-500"
                  style={{ width: `${batch.total ? Math.round(((batch.hechos || 0) / batch.total) * 100) : 0}%` }}
                />
              </div>
              {(batch.errores || []).length > 0 && (
                <p className="text-xs text-amber-600 mt-2" data-testid="p8-form-batch-errores">
                  {batch.errores.length} con error: {batch.errores.map((e) => e.trabajador).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!generado && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> Genera primero el Plan de PRL completo (Paso 7).
        </div>
      )}
      {generado && trabajadores.length === 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> Añade al menos un trabajador en el Paso 3 (Puestos y trabajadores).
        </div>
      )}

      {trabajadores.length === 0 ? (
        <p className="text-sm text-slate-400">Sin trabajadores registrados.</p>
      ) : (
        <div className="space-y-3">
          {trabajadores.map((t) => {
            const d = docFor(t);
            const f = formFor(t);
            return (
              <div key={t.id} data-testid={`p8-trab-${t.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-blue-700" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-slate-900">{t.nombre || "(sin nombre)"}</div>
                  <div className="text-sm text-slate-500">{t.puesto || "Sin puesto asignado"}</div>
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  {(d || f) && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => borrarDocs(t)}
                        disabled={!!delLoading[t.id]}
                        data-testid={`p8-borrar-${t.id}`}
                        title="Borrar los documentos generados de este trabajador para regenerarlos"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-40"
                      >
                        {delLoading[t.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Borrar documentos
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400 w-24 shrink-0">Info. puesto</span>
                    {d ? (
                      <>
                        <a href={`${BACKEND}${d.docx_url}`} target="_blank" rel="noreferrer" data-testid={`p8-docx-${t.id}`} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><FileText className="w-4 h-4 text-blue-700" /> Word</a>
                        <a href={`${BACKEND}${d.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`p8-pdf-${t.id}`} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><FileText className="w-4 h-4 text-red-600" /> PDF</a>
                        {d.generado_at && <span className="text-[11px] text-slate-400 ml-1" data-testid={`p8-fecha-${t.id}`}>{fdt(d.generado_at)}</span>}
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">Pendiente de generar</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400 w-24 shrink-0">Formación IA</span>
                    <input
                      type="number" min="1" value={formDur[t.id] || ""}
                      onChange={(e) => setFormDur((s) => ({ ...s, [t.id]: e.target.value }))}
                      placeholder="min (auto)"
                      data-testid={`p8-form-dur-${t.id}`}
                      title="Duración en minutos (vacío = cálculo automático)"
                      className="w-24 h-9 px-2 rounded-lg border border-slate-300 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                    />
                    <button
                      onClick={() => generarForm(t)}
                      disabled={!!formLoading[t.id]}
                      data-testid={`p8-form-gen-${t.id}`}
                      className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-purple-600 text-purple-700 text-sm font-medium hover:bg-purple-50 disabled:opacity-40"
                    >
                      {formLoading[t.id] ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</> : <><Sparkles className="w-4 h-4" /> {f ? "Regenerar" : "Generar (IA)"}</>}
                    </button>
                    {f && (
                      <>
                        <a href={`${BACKEND}${f.docx_url}`} target="_blank" rel="noreferrer" data-testid={`p8-form-docx-${t.id}`} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><FileText className="w-4 h-4 text-blue-700" /> Word</a>
                        <a href={`${BACKEND}${f.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`p8-form-pdf-${t.id}`} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><FileText className="w-4 h-4 text-red-600" /> PDF</a>
                        {f.revision_pendiente && <span className="text-xs text-amber-600" title="Hay apartados marcados para revisión técnica">⚠ revisar</span>}
                        {f.generado_at && <span className="text-[11px] text-slate-400 ml-1" data-testid={`p8-form-fecha-${t.id}`}>{fdt(f.generado_at)}</span>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
